import React, { Component } from "react";
import moment, { Moment, Duration } from "moment";
import copy from "copy-to-clipboard";

import styles from "./Sidebar.css";
import messages from "./locales";

import { VehicleListItem } from "./components/ListItem/VehicleListItem";
import { DestinyListItem } from "./components/ListItem/DestinityListItem";
import { ETADisplayer } from "./components/ETADisplayer/ETADisplayer";
import { ArrivedDisplayer } from "./components/ArrivedDisplayer/ArrivedDisplayer";

import Info from "./images/ic-info.svg";
import Cobli from "./images/ic-cobli.svg";

import LinkIcon from "./images/ic-link.svg";
import WhatsappIcon from "./images/ic-whatsapp.svg";
import SharedIcon from "./images/share.svg";

import { Realtime } from "./components/Realtime/Realtime";
import { ServiceStatus } from "./components/ServiceStatus/ServiceStatus";
import { Button } from "./components/Button/Button";
import { isMobile } from "../../isMobile";

interface Props {
  fleetName: string;
  brand: string | null;
  model: string | null;
  speed: number;
  ignitionOn: boolean;
  address: string;
  driverName: null | string;
  vehicleRegistrationPlate: string | null;
  arrived: boolean;
  arrivalTime: number;
  eta: number;
  lastUpdatedTime: number;
  isRealTimeUpdated: boolean;
  remainingDistance: string;
  estimatedDuration: Duration;
  estimatedTimeOfArrival: Moment;
}

interface State {
  clickedCopyButton: boolean;
}

export class Sidebar extends Component<Props, State> {
  hourInSecs: number;
  minuteInSecs: number;
  hourInMinutes: number;
  footRef?: HTMLDivElement | null;
  constructor(props: Props) {
    super(props);
    this.hourInSecs = 60 * 60;
    this.minuteInSecs = 60;
    this.hourInMinutes = 60;
    this.state = {
      clickedCopyButton: false
    };
  }

  static defaultProps: Partial<Props> = {
    arrivalTime: -1,
    eta: 0,
    driverName: undefined
  };

  formatTime(time: number): string {
    return time >= 10 ? String(time) : "0" + time;
  }

  textFormattedTime = (time: number) => {
    const dateTime = moment(time);
    const hour = dateTime.hour();
    const minutes = dateTime.minute();

    return this.formatTime(hour) + ":" + this.formatTime(minutes);
  };

  whatsappShareUrl() {
    const desktopPrefix = "https://web.whatsapp.com";
    const mobilePrefix = "whatsapp";

    return `${isMobile() ? mobilePrefix : desktopPrefix}://send?text=${
      window.location.href
    }`;
  }

  renderSharedLink = () => (
    <div className={styles.sharedLink}>
      <hr className={styles.divider} />
      <div className={styles.titleSharedLink}>
        <SharedIcon className={styles.sharedIcon} />
        Compartilhe essa página
      </div>
      <div className={styles.buttonsContainer}>
        <Button
          className={styles.copyButton}
          onClick={() => {
            copy(window.location.href);
            this.setState({ clickedCopyButton: true });
          }}
          icon={LinkIcon}
          text={
            this.state.clickedCopyButton
              ? messages.link_copied
              : messages.link_not_copied
          }
        />

        <Button
          onClick={() => window.open(this.whatsappShareUrl())}
          icon={WhatsappIcon}
          text={messages.whatsapp}
          className={styles.whatsappButton}
        />
      </div>
    </div>
  );

  renderTextInfo = () => (
    <div className={styles.infoTextContainer}>
      <Info className={styles.infoIcon} />
      <div className={styles.infoText}>
        Por motivos de segurança, as informações dessa página expiram após o
        veículo chegar no local previsto ou após 24 horas.
      </div>
    </div>
  );

  renderListItems = () => (
    <div className={styles.containerListItems}>
      <div className={styles.listItemsTitle}>Detalhes do serviço</div>
      <DestinyListItem address={this.props.address} />
      <VehicleListItem
        arrived={this.props.arrived}
        vehicleRegistrationPlate={this.props.vehicleRegistrationPlate}
        ignitionOn={this.props.ignitionOn}
        brand={this.props.brand}
        model={this.props.model}
        speed={this.props.speed}
        driverName={this.props.driverName}
        isRealTimeUpdated={this.props.isRealTimeUpdated}
      />
    </div>
  );

  renderFooter = () => (
    <div className={styles.footer}>
      Rastreado por
      <Cobli className={styles.footerCobliIcon} />
    </div>
  );

  scrollToServiceStatus = () => {
    if (this.footRef) {
      this.footRef.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "end"
      });
    }
  };

  setServiceStatusRef = (ref: HTMLDivElement | null) => {
    if (!ref) return;
    this.footRef = ref;
    window.document.addEventListener(
      "scroll-pagineta",
      this.scrollToServiceStatus
    );
  };

  render() {
    return (
      <>
        <div className={styles.sidebar}>
          {this.props.arrived ? (
            <ArrivedDisplayer
              arrivalTime={this.props.arrivalTime}
              onClickChevronDown={this.scrollToServiceStatus}
            />
          ) : (
            <ETADisplayer
              estimatedTimeOfArrival={this.props.estimatedTimeOfArrival}
              remainingDistance={this.props.remainingDistance}
              estimatedDuration={this.props.estimatedDuration}
            />
          )}
          {!this.props.arrived && (
            <Realtime
              isRealTimeUpdated={this.props.isRealTimeUpdated}
              lastUpdatedTime={this.props.lastUpdatedTime}
              onClickChevronDown={this.scrollToServiceStatus}
            />
          )}
          <div
            className={styles.containerStatus}
            ref={this.setServiceStatusRef}
          >
            <ServiceStatus
              fleetName={this.props.fleetName}
              arrived={this.props.arrived}
            />
            <hr className={styles.divider} />
            {this.renderListItems()}
            {this.renderSharedLink()}
            <div className={styles.containerFooter}>
              {this.renderTextInfo()}
              {this.renderFooter()}
            </div>
          </div>
        </div>
      </>
    );
  }
}
